import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface HealthMetrics {
  webhook_success_rate: number;
  webhooks_last_24h: number;
  webhook_errors_last_24h: number;
  forms_created_today: number;
  responses_today: number;
  active_users_today: number;
}

export interface WebhookLog {
  id: string;
  status: string;
  response_status_code: number | null;
  request_payload: Record<string, unknown> | null;
  response_body: string | null;
  error_message: string | null;
  attempt_number: number;
  created_at: string;
  webhook_url: string;
  webhook_name: string;
  form_id: string;
  form_title: string;
  user_email: string;
}

interface UseWebhookLogsParams {
  status?: string | null;
  formId?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  page?: number;
}

export function useHealthMetrics(refreshInterval: number = 60000) {
  const [metrics, setMetrics] = useState<HealthMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      const { data, error: e } = await supabase.rpc('admin_get_health_metrics', {});
      if (e) setError(e.message);
      else if (data) setMetrics(data);
      setLoading(false);
    };

    fetchMetrics();
    const interval = setInterval(fetchMetrics, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  return { metrics, loading, error };
}

export function useWebhookLogs({ status, formId, dateFrom, dateTo, page = 1 }: UseWebhookLogsParams) {
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      const { data, error: e } = await supabase.rpc('admin_list_webhook_logs', {
        p_status: status || null,
        p_form_id: formId || null,
        p_date_from: dateFrom || null,
        p_date_to: dateTo || null,
        p_page: page,
        p_per_page: 50,
      });
      if (e) setError(e.message);
      else { setLogs(data.logs); setTotal(data.total); }
      setLoading(false);
    };
    fetchLogs();
  }, [status, formId, dateFrom, dateTo, page]);

  return { logs, total, loading, error };
}

// =====================================================
// SYSTEM LOGS — PERSISTENT ERROR TRACKING
// =====================================================

export interface SystemLog {
  id: string;
  user_id: string | null;
  user_email: string | null;
  level: 'error' | 'warn' | 'info';
  category: string;
  message: string;
  metadata: Record<string, unknown>;
  user_agent: string | null;
  created_at: string;
}

export interface ErrorMetrics {
  errors_last_24h: number;
  warns_last_24h: number;
  total_last_24h: number;
  top_categories: Array<{ category: string; count: number }>;
  top_affected_users: Array<{ user_id: string; email: string; count: number }>;
}

interface UseSystemLogsParams {
  level?: string | null;
  category?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  page?: number;
}

export function useErrorMetrics(refreshInterval: number = 60000) {
  const [metrics, setMetrics] = useState<ErrorMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      const { data, error: e } = await supabase.rpc('admin_get_error_metrics', {});
      if (e) setError(e.message);
      else if (data) setMetrics(data);
      setLoading(false);
    };

    fetchMetrics();
    const interval = setInterval(fetchMetrics, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  return { metrics, loading, error };
}

export function useSystemLogs({ level, category, dateFrom, dateTo, page = 1 }: UseSystemLogsParams) {
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      const { data, error: e } = await supabase.rpc('admin_list_system_logs', {
        p_level: level || null,
        p_category: category || null,
        p_user_id: null,
        p_date_from: dateFrom || null,
        p_date_to: dateTo || null,
        p_page: page,
        p_per_page: 50,
      });
      if (e) setError(e.message);
      else { setLogs(data.logs); setTotal(data.total); }
      setLoading(false);
    };
    fetchLogs();
  }, [level, category, dateFrom, dateTo, page]);

  return { logs, total, loading, error };
}
